import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";
import AuthShell, { AuthField } from "../components/auth/AuthShell";
import { GameButton, GameIcon } from "../components/game";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The server says "Invalid or expired reset token" in English for a link that
  // is old, already used or replaced. Show Romanian text and a way forward.
  const [linkExpired, setLinkExpired] = useState(false);

  usePageTitle(t("auth.reset_password_title", "Setează parolă nouă"));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);

    if (password.length < 6) {
      setErr(t("auth.reset_password_too_short", "Parola trebuie să aibă cel puțin 6 caractere."));
      return;
    }
    if (password !== confirm) {
      setErr(t("auth.reset_password_mismatch", "Parolele nu coincid."));
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (e: any) {
      const serverMsg: string | undefined = e?.response?.data?.error;
      if (e?.response?.status === 400 && /invalid or expired/i.test(serverMsg || "")) {
        setLinkExpired(true);
      } else {
        setErr(serverMsg || t("auth.reset_password_error", "Ceva nu a mers. Încearcă din nou."));
      }
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <AuthShell icon={<GameIcon name="bolt" className="w-8 h-8 text-rose-500" />} title={t("auth.reset_password_title", "Setează parolă nouă")}>
        <div className="text-center">
          <p className="text-rose-600 mb-4 text-sm">{t("auth.reset_password_no_token", "Link invalid — lipsește tokenul.")}</p>
          <Link to="/forgot-password" className="font-semibold text-indigo-600 hover:text-indigo-700">
            {t("auth.request_new_reset", "Cere o resetare nouă")}
          </Link>
        </div>
      </AuthShell>
    );
  }

  if (linkExpired) {
    return (
      <AuthShell icon={<GameIcon name="bolt" className="w-8 h-8 text-rose-500" />} title={t("auth.reset_password_title", "Setează parolă nouă")}>
        <div className="text-center">
          <p className="text-rose-600 mb-4 text-sm">
            {t("auth.reset_link_expired", "Linkul a expirat sau a fost deja folosit.")}
          </p>
          <Link to="/forgot-password" className="font-semibold text-indigo-600 hover:text-indigo-700">
            {t("auth.reset_link_request_new", "Trimite-mi un link nou")}
          </Link>
        </div>
        <div className="mt-6 text-sm text-center">
          <Link to="/login" className="font-semibold text-indigo-600 hover:text-indigo-700">
            {t("auth.back_to_login", "Înapoi la login")}
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      icon={<GameIcon name="bulb" className="w-8 h-8" />}
      title={t("auth.reset_password_title", "Setează parolă nouă")}
      subtitle={done ? undefined : t("auth.reset_password_subtitle", "Alege o parolă cu cel puțin 6 caractere.")}
    >
      {done ? (
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-100 grid place-items-center animate-zoom">
            <GameIcon name="check" className="w-8 h-8 text-emerald-600" />
          </div>
          <p className="text-slate-600 text-sm">
            {t("auth.reset_password_success", "Parolă schimbată. Te redirecționăm la login...")}
          </p>
        </div>
      ) : (
        // autoComplete="new-password" on both password inputs is the reliable
        // way to disable browser/password-manager autofill on this form —
        // without it a saved credential from another site could overwrite what
        // the user typed and the backend would hash the wrong value.
        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          {err && (
            <div className="p-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl">{err}</div>
          )}
          <AuthField
            label={t("auth.new_password", "Parolă nouă")}
            type="password"
            name="new-password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
            placeholder="••••••••"
          />
          <AuthField
            label={t("auth.confirm_password", "Confirmă parola")}
            type="password"
            name="confirm-new-password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            placeholder="••••••••"
          />
          <GameButton type="submit" disabled={loading}>
            {loading ? t("common.loading", "Se salvează...") : t("auth.reset_password_submit", "Schimbă parola")}
          </GameButton>
        </form>
      )}

      <div className="mt-6 text-sm text-center">
        <Link to="/login" className="font-semibold text-indigo-600 hover:text-indigo-700">
          {t("auth.back_to_login", "Înapoi la login")}
        </Link>
      </div>
    </AuthShell>
  );
}
